import React, { useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronUp,
  ListChecks,
  X,
  Sparkles,
  Target,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface OnboardingChecklistProps {
  completed: { added: boolean; uploaded: boolean; invited: boolean };
  /** Live counts from the family graph — used to award fun "goals". */
  peopleCount: number;
  mediaCount: number;
  hasParent: boolean;
  onOpenGuide: () => void;
  onDismiss: () => void;
}

/**
 * Small, dismissible progress card pinned to the bottom-right corner.
 * Visible only for logged-in subscribed users until all 3 core steps are
 * done (or the user explicitly dismisses it). Clicking the card opens the
 * full guided tour.
 *
 * The card now has two layers:
 *   1. Core checklist — Add member / Upload memory / Invite family
 *   2. Goals — small fun milestones (first parent, 3 members, memory to
 *      a person) that update live as the user adds data.
 */
const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({
  completed,
  peopleCount,
  mediaCount,
  hasParent,
  onOpenGuide,
  onDismiss,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  const items: Array<{ key: keyof typeof completed; label: string }> = [
    { key: 'added', label: 'Add a family member' },
    { key: 'uploaded', label: 'Upload a memory' },
    { key: 'invited', label: 'Invite your family' },
  ];

  const doneCount =
    (completed.added ? 1 : 0) +
    (completed.uploaded ? 1 : 0) +
    (completed.invited ? 1 : 0);
  const total = items.length;
  const pct = Math.round((doneCount / total) * 100);

  // Live "goals" — small milestones that feel rewarding to hit. These come
  // from the actual family graph state, not localStorage flags, so they
  // light up the moment the user makes progress.
  const goals: Array<{ key: string; label: string; done: boolean }> = [
    { key: 'parent', label: 'Add your first parent', done: hasParent },
    {
      key: 'three',
      label: `Add 3 family members (${Math.min(peopleCount, 3)}/3)`,
      done: peopleCount >= 3,
    },
    {
      key: 'memory',
      label: 'Upload a memory to someone',
      done: mediaCount >= 1,
    },
  ];

  return (
    <div className="fixed bottom-4 right-4 z-40 w-[calc(100%-2rem)] sm:w-80 max-w-sm">
      <div className="rounded-2xl bg-white shadow-xl border border-fuchsia-100 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-fuchsia-50 to-pink-50 border-b border-fuchsia-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center text-white">
              <ListChecks className="w-4 h-4" />
            </div>
            <div>
              <div className="text-sm font-bold text-slate-900 leading-tight">
                Your family is{' '}
                <span className="text-fuchsia-700">{pct}%</span> started
              </div>
              <div className="text-[11px] text-slate-500">
                {doneCount} of {total} steps done
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? 'Expand' : 'Collapse'}
              className="p-1.5 rounded-full hover:bg-white/70 text-slate-600"
            >
              {collapsed ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={onDismiss}
              aria-label="Dismiss checklist"
              className="p-1.5 rounded-full hover:bg-white/70 text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {!collapsed && (
          <div className="px-4 py-3">
            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden mb-3">
              <div
                className="h-full bg-gradient-to-r from-fuchsia-500 to-pink-500 transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <ul className="space-y-2">
              {items.map((it) => {
                const done = completed[it.key];
                return (
                  <li key={it.key} className="flex items-center gap-2.5 text-sm">
                    <span
                      className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                        done
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-100 text-slate-400 border border-slate-200'
                      }`}
                    >
                      {done && <Check className="w-3 h-3" />}
                    </span>
                    <span
                      className={
                        done
                          ? 'text-slate-500 line-through'
                          : 'text-slate-800 font-medium'
                      }
                    >
                      {it.label}
                    </span>
                  </li>
                );
              })}
            </ul>

            {/* Fun goals */}
            <div className="mt-4 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-1.5 mb-2">
                <Target className="w-3.5 h-3.5 text-fuchsia-600" />
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                  Little goals
                </div>
              </div>
              <ul className="space-y-1.5">
                {goals.map((g) => (
                  <li
                    key={g.key}
                    className="flex items-center gap-2 text-xs"
                  >
                    <span
                      className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${
                        g.done
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-100 text-slate-400 border border-slate-200'
                      }`}
                    >
                      {g.done ? (
                        <Check className="w-2.5 h-2.5" />
                      ) : (
                        <Sparkles className="w-2.5 h-2.5" />
                      )}
                    </span>
                    <span
                      className={
                        g.done
                          ? 'text-slate-500 line-through'
                          : 'text-slate-700'
                      }
                    >
                      {g.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <Button
              onClick={onOpenGuide}
              className="w-full mt-3 rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white text-sm py-2"
            >
              Open guide
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingChecklist;
